<template>
  <app-collapse class="mt-1">
    <app-collapse-item
      title="Seller information"
      :is-visible="false"
      class="collapsible-section-bg"
    >
      <template #header>
        <span
          class="lead collapse-title"
        >
          <section-status-icon
            :has-data="hasSellerData"
            :has-errors="hasSectionErrors"
          />
          Seller information
          <feather-icon
            v-b-tooltip.hover.top="'If the seller and exporter are the same, it is not necessary to provide the information of the seller.'"
            icon="InfoIcon"
            size="14"
            class="ml-50 text-muted"
          />
        </span>
      </template>

      <validation-observer>
        <!-- Seller Section -->
        <b-row v-if="parcel.seller">
          <b-col
            lg="3"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Seller name"
            >
              <label>Seller name</label>
              <b-form-input
                v-model="parcel.seller.name"
                v-b-tooltip.hover.top="getIssueMessage('seller.name')"
                :class="getIssueClass('seller.name')"
                :disabled="disabled"
                class="mr-0 ml-0"
                :state="errors.length > 0 ? false:null"
              />
            </validation-provider>
          </b-col>

          <b-col
            lg="3"
            md="4"
          >
            <label>Seller registry number</label>
            <b-form-input
              v-model="parcel.seller.registry_number"
              v-b-tooltip.hover.top="getIssueMessage('seller.registry_number')"
              :class="getIssueClass('seller.registry_number')"
              :disabled="disabled"
              class="mr-0 ml-0"
            />
          </b-col>

          <b-col
            lg="3"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Seller street number"
            >
              <label>Seller street number</label>
              <b-form-input
                v-model="parcel.seller.street_and_number"
                v-b-tooltip.hover.top="getIssueMessage('seller.street_and_number')"
                :class="getIssueClass('seller.street_and_number')"
                :disabled="disabled"
                class="mr-0 ml-0"
                :state="errors.length > 0 ? false:null"
              />
            </validation-provider>
          </b-col>

          <b-col
            lg="3"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Seller postal code"
              rules="max:20"
            >
              <label>Seller postal code</label>
              <b-form-input
                v-model="parcel.seller.postal_code"
                v-b-tooltip.hover.top="getIssueMessage('seller.postal_code')"
                :class="getIssueClass('seller.postal_code')"
                :disabled="disabled"
                class="mr-0 ml-0"
                :state="errors.length > 0 ? false:null"
              />
            </validation-provider>
          </b-col>

          <b-col
            lg="3"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Seller city"
            >
              <label>Seller city</label>
              <b-form-input
                v-model="parcel.seller.city"
                v-b-tooltip.hover.top="getIssueMessage('seller.city')"
                :class="getIssueClass('seller.city')"
                :disabled="disabled"
                class="mr-0 ml-0"
                :state="errors.length > 0 ? false:null"
              />
            </validation-provider>
          </b-col>

          <b-col
            lg="3"
            md="4"
          >
            <label>Seller region</label>
            <b-form-input
              v-model="parcel.seller.region"
              v-b-tooltip.hover.top="getIssueMessage('seller.region')"
              :class="getIssueClass('seller.region')"
              :disabled="disabled"
              class="mr-0 ml-0"
            />
          </b-col>

          <b-col
            lg="3"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Seller country"
              rules="min:2|max:2|alpha"
            >
              <label>Seller country</label>
              <classifier-select
                v-model="parcel.seller.country"
                v-b-tooltip.hover.top="getIssueMessage('seller.country')"
                :classifier-key="classifierKeys.countries"
                :class="getIssueClass('seller.country')"
                :disabled="disabled"
                class="mr-0 ml-0"
                :state="errors.length > 0 ? false:null"
              />
            </validation-provider>
          </b-col>
        </b-row>
      </validation-observer>
    </app-collapse-item>
  </app-collapse>
</template>

<script>
import {
  BRow,
  BCol,
  BFormInput,
  VBTooltip,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import errorHighlightingMixin from '@/mixins/errorHighlightingMixin'
import SectionStatusIcon from '@/views/shipments/declarations/modals/components/h1/SectionStatusIcon.vue'
import ClassifierSelect from '@/views/components/ClassifierSelect.vue'
import { CUSTOMS_CLASSIFIER_KEYS } from '@/libs/customsClassifiers'

export default {
  components: {
    BRow,
    BCol,
    BFormInput,
    ValidationObserver,
    ValidationProvider,
    AppCollapse,
    AppCollapseItem,
    SectionStatusIcon,
    ClassifierSelect,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [errorHighlightingMixin],
  props: {
    parcel: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return { classifierKeys: CUSTOMS_CLASSIFIER_KEYS }
  },
  computed: {
    hasSellerData() {
      if (!this.parcel || !this.parcel.seller) {
        return false
      }
      return !!(this.parcel.seller.name || this.parcel.seller.registry_number || this.parcel.seller.street_and_number || this.parcel.seller.postal_code || this.parcel.seller.city || this.parcel.seller.region || this.parcel.seller.country)
    },
    hasSectionErrors() {
      return Object.keys(this.issuesMap).some(key => key.startsWith('seller.'))
    },
  },
}
</script>

<style scoped lang="scss">
@import '@core/scss/base/bootstrap-extended/variables';
@import '@core/scss/base/core/mixins/declarations-mixins';

.collapsible-section-bg {
  @include collapsible-section-bg;
}

.input-warning {
  @include input-warning;
}

.input-danger {
  @include input-danger;
}
</style>
